import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Crown, Search, CheckCircle, XCircle, Ban, Eye, Edit, Users, RefreshCw, ExternalLink, Link, AlertCircle, ShieldCheck, Trash2, KeyRound, Plus, Copy, Phone, FileImage } from 'lucide-react';
import type { Database } from '@/integrations/supabase/types';

type AmbassadorApplication = Database['public']['Tables']['ambassador_applications']['Row'];
type AmbassadorStatus = Database['public']['Enums']['ambassador_status_enum'];
type AmbassadorTier = Database['public']['Enums']['ambassador_tier_enum'];
type AmbassadorPlatform = Database['public']['Tables']['ambassador_platforms']['Row'];
type InviteCode = Database['public']['Tables']['ambassador_invite_codes']['Row'];

const STATUS_CONFIG: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: React.ReactNode }> = {
  pending_verification: { label: 'Pending', variant: 'secondary', icon: <Eye className="h-3 w-3" /> },
  approved: { label: 'Approved', variant: 'default', icon: <CheckCircle className="h-3 w-3" /> },
  rejected: { label: 'Rejected', variant: 'destructive', icon: <XCircle className="h-3 w-3" /> },
  suspended: { label: 'Suspended', variant: 'outline', icon: <Ban className="h-3 w-3" /> },
};

const TIER_COLORS: Record<string, string> = {
  nano: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  micro: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  macro: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  mega: 'bg-purple-500/15 text-purple-600 border-purple-500/30',
};

// Inline component to load ID document via signed URL
const IDDocumentViewer: React.FC<{ documentPath: string }> = ({ documentPath }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUrl = async () => {
      setLoading(true);
      try {
        const parts = documentPath.split('/');
        const bucketName = parts[0];
        const filePath = parts.slice(1).join('/');

        const { data, error: fnError } = await supabase.functions.invoke('get-provider-documents', {
          body: { providerId: '__ambassador__', documentType: 'ambassador_id', bucketName, filePath }
        });

        if (fnError || data?.error) {
          setError('Could not load document');
        } else {
          setUrl(data?.url || null);
        }
      } catch {
        setError('Could not load document');
      }
      setLoading(false);
    };
    fetchUrl();
  }, [documentPath]);

  if (loading) return <Skeleton className="h-48 w-full" />;
  if (error || !url) return <p className="text-xs text-muted-foreground">{error || 'No document available'}</p>;

  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="block">
      <img src={url} alt="ID Document" className="max-h-64 rounded-lg border object-contain w-full" />
      <p className="text-[10px] text-muted-foreground mt-1 text-center">Click to open full size</p>
    </a>
  );
};

const AdminAmbassadorsPanel: React.FC = () => {
  const { t } = useTranslation();
  const [applications, setApplications] = useState<AmbassadorApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterTier, setFilterTier] = useState<string>('all');
  const [selectedApp, setSelectedApp] = useState<AmbassadorApplication | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState<{ tier: string; status: string }>({ tier: '', status: '' });
  const [saving, setSaving] = useState(false);
  const [platformsMap, setPlatformsMap] = useState<Record<string, AmbassadorPlatform[]>>({});

  // Invite codes state
  const [inviteCodes, setInviteCodes] = useState<InviteCode[]>([]);
  const [showInviteCodes, setShowInviteCodes] = useState(false);
  const [newCodeInput, setNewCodeInput] = useState('');
  const [newCodeDescription, setNewCodeDescription] = useState('');
  const [newCodeExpiresAt, setNewCodeExpiresAt] = useState('');
  const [deletingCodeId, setDeletingCodeId] = useState<string | null>(null);
  const [codeToDelete, setCodeToDelete] = useState<InviteCode | null>(null);

  const fetchApplications = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('ambassador_applications')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      toast.error(t('adminPanel.ambassadors.errorLoading', 'Error loading ambassadors'));
    } else {
      setApplications(data || []);
      // Fetch platforms for all ambassadors
      if (data && data.length > 0) {
        const ids = data.map(a => a.id);
        const { data: plats } = await supabase.from('ambassador_platforms').select('*').in('ambassador_id', ids);
        const map: Record<string, AmbassadorPlatform[]> = {};
        (plats || []).forEach(p => {
          if (!map[p.ambassador_id]) map[p.ambassador_id] = [];
          map[p.ambassador_id].push(p);
        });
        setPlatformsMap(map);
      }
    }
    setLoading(false);
  };

  const fetchInviteCodes = async () => {
    const { data } = await supabase.from('ambassador_invite_codes').select('*').order('created_at', { ascending: false });
    setInviteCodes(data || []);
  };

  useEffect(() => { fetchApplications(); fetchInviteCodes(); }, []);

  const filtered = applications.filter(app => {
    const matchesSearch = !searchTerm ||
      app.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.referral_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.paypal_email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
    const matchesTier = filterTier === 'all' || app.tier === filterTier;
    return matchesSearch && matchesStatus && matchesTier;
  });

  const generateReferralCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = 'PETIUS-';
    for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
    return code;
  };

  const updateStatus = async (id: string, newStatus: AmbassadorStatus) => {
    setSaving(true);
    const updateData: Record<string, unknown> = { status: newStatus };
    if (newStatus === 'approved') {
      updateData.approved_at = new Date().toISOString();
      // Generate referral code if none exists
      const app = applications.find(a => a.id === id);
      if (app && !app.referral_code) {
        updateData.referral_code = generateReferralCode();
      }
    }

    const { error } = await supabase
      .from('ambassador_applications')
      .update(updateData)
      .eq('id', id);

    if (error) {
      toast.error(t('adminPanel.ambassadors.updateError', 'Error updating status'));
    } else {
      toast.success(t('adminPanel.ambassadors.statusUpdated', 'Status updated successfully'));
      fetchApplications();
      setDetailOpen(false);
    }
    setSaving(false);
  };

  const [appToDelete, setAppToDelete] = useState<{ id: string; name: string } | null>(null);

  const confirmDeleteApplication = async () => {
    if (!appToDelete) return;
    setSaving(true);
    const { error } = await supabase.from('ambassador_applications').delete().eq('id', appToDelete.id);
    if (error) {
      toast.error(t('adminPanel.ambassadors.deleteError', 'Error deleting application'));
    } else {
      toast.success(t('adminPanel.ambassadors.deleteSuccess', 'Application deleted successfully'));
      fetchApplications();
      setDetailOpen(false);
    }
    setSaving(false);
    setAppToDelete(null);
  };

  const saveEdit = async () => {
    if (!selectedApp) return;
    setSaving(true);
    const updateData: Record<string, unknown> = {
      tier: editData.tier as AmbassadorTier,
      status: editData.status as AmbassadorStatus,
    };
    if (editData.status === 'approved' && selectedApp.status !== 'approved' && !selectedApp.referral_code) {
      updateData.referral_code = generateReferralCode();
      updateData.approved_at = new Date().toISOString();
    }
    const { error } = await supabase.from('ambassador_applications').update(updateData).eq('id', selectedApp.id);
    if (error) {
      toast.error(t('adminPanel.ambassadors.updateError', 'Error updating ambassador'));
    } else {
      toast.success(t('adminPanel.ambassadors.editSuccess', 'Ambassador updated successfully'));
      fetchApplications();
      setEditOpen(false);
    }
    setSaving(false);
  };

  const togglePlatformVerification = async (platformId: string, currentValue: boolean) => {
    const { error } = await supabase
      .from('ambassador_platforms')
      .update({
        verified_by_admin: !currentValue,
        verified_at: !currentValue ? new Date().toISOString() : null
      })
      .eq('id', platformId);
    if (error) {
      toast.error('Error updating verification');
    } else {
      toast.success(!currentValue ? 'Platform verified' : 'Verification removed');
      fetchApplications();
    }
  };

  const createInviteCode = async () => {
    const code = newCodeInput.trim().toUpperCase() || generateReferralCode().replace('PETIUS-', 'INV-');
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const insertData: Record<string, unknown> = { code, created_by: user.id };
    if (newCodeDescription.trim()) insertData.description = newCodeDescription.trim();
    if (newCodeExpiresAt) insertData.expires_at = new Date(newCodeExpiresAt).toISOString();
    const { error } = await supabase.from('ambassador_invite_codes').insert(insertData as any);
    if (error) {
      toast.error(error.message.includes('duplicate') ? 'Code already exists' : 'Error creating code');
    } else {
      toast.success(`Invite code "${code}" created`);
      setNewCodeInput('');
      setNewCodeDescription('');
      setNewCodeExpiresAt('');
      fetchInviteCodes();
    }
  };

  const deleteInviteCode = async (id: string) => {
    setDeletingCodeId(id);
    const { error } = await supabase.from('ambassador_invite_codes').delete().eq('id', id);
    if (error) {
      toast.error('Error deleting invite code');
    } else {
      toast.success('Invite code deleted');
      fetchInviteCodes();
    }
    setDeletingCodeId(null);
  };

  const openDetail = (app: AmbassadorApplication) => { setSelectedApp(app); setDetailOpen(true); };
  const openEdit = (app: AmbassadorApplication) => { setSelectedApp(app); setEditData({ tier: app.tier, status: app.status }); setEditOpen(true); };

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending_verification').length,
    approved: applications.filter(a => a.status === 'approved').length,
    suspended: applications.filter(a => a.status === 'suspended').length,
  };

  if (loading) {
    return <Card><CardContent className="p-6 space-y-4">{[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}</CardContent></Card>;
  }

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold">{stats.total}</p><p className="text-xs text-muted-foreground">Total</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-yellow-500">{stats.pending}</p><p className="text-xs text-muted-foreground">Pending</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-green-500">{stats.approved}</p><p className="text-xs text-muted-foreground">Approved</p></CardContent></Card>
        <Card><CardContent className="p-3 text-center"><p className="text-2xl font-bold text-red-500">{stats.suspended}</p><p className="text-xs text-muted-foreground">Suspended</p></CardContent></Card>
      </div>

      {/* Invite Code Management */}
      <Card>
        <CardHeader className="p-3 sm:p-4 cursor-pointer" onClick={() => setShowInviteCodes(!showInviteCodes)}>
          <CardTitle className="text-base flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            {t('adminPanel.ambassadors.inviteCodes', 'Invitation Codes')}
            <Badge variant="secondary" className="ml-auto">{inviteCodes.length}</Badge>
          </CardTitle>
        </CardHeader>
        {showInviteCodes && (
          <CardContent className="p-3 sm:p-4 pt-0 space-y-3">
            <div className="space-y-2">
              <div className="flex gap-2">
                <Input placeholder="Custom code (optional)" value={newCodeInput} onChange={(e) => setNewCodeInput(e.target.value.toUpperCase())} className="font-mono uppercase" />
                <Button onClick={createInviteCode} size="sm"><Plus className="h-4 w-4 mr-1" /> Generate</Button>
              </div>
              <Input placeholder="Description (optional)" value={newCodeDescription} onChange={(e) => setNewCodeDescription(e.target.value)} />
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground whitespace-nowrap">Expires:</span>
                <Input type="date" value={newCodeExpiresAt} onChange={(e) => setNewCodeExpiresAt(e.target.value)} className="flex-1" />
              </div>
            </div>
            <div className="divide-y max-h-72 overflow-y-auto">
              {inviteCodes.map(ic => {
                const isExpired = (ic as any).expires_at && new Date((ic as any).expires_at) < new Date();
                return (
                  <div key={ic.id} className="py-2 space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <code className="font-mono font-bold">{ic.code}</code>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(ic.code); toast.success('Copied'); }}>
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {ic.used_by ? (
                          <Badge variant="outline" className="text-[9px]">Used</Badge>
                        ) : isExpired ? (
                          <Badge variant="destructive" className="text-[9px]">Expired</Badge>
                        ) : ic.is_active ? (
                          <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-[9px]" variant="outline">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[9px]">Inactive</Badge>
                        )}
                        <span>{format(new Date(ic.created_at), 'MMM dd')}</span>
                        {!ic.used_by && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                            disabled={deletingCodeId === ic.id}
                            onClick={() => setCodeToDelete(ic)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {((ic as any).description || (ic as any).expires_at) && (
                      <div className="flex flex-wrap gap-x-3 text-[10px] text-muted-foreground pl-1">
                        {(ic as any).description && <span>{(ic as any).description}</span>}
                        {(ic as any).expires_at && (
                          <span className={isExpired ? 'text-destructive' : ''}>
                            Expires: {format(new Date((ic as any).expires_at), 'MMM dd, yyyy')}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        )}
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-3 sm:p-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search by name, email, code..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-[140px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="pending_verification">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterTier} onValueChange={setFilterTier}>
              <SelectTrigger className="w-full sm:w-[120px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="nano">Nano</SelectItem>
                <SelectItem value="micro">Micro</SelectItem>
                <SelectItem value="macro">Macro</SelectItem>
                <SelectItem value="mega">Mega</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="icon" onClick={fetchApplications}><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Applications List */}
      <Card>
        <CardHeader className="p-3 sm:p-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Crown className="h-5 w-5" />
            Ambassador Applications
            <Badge variant="secondary" className="ml-auto">{filtered.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">No applications found</div>
          ) : (
            <div className="divide-y">
              {filtered.map((app) => {
                const statusConfig = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending_verification;
                const tierColor = TIER_COLORS[app.tier] || TIER_COLORS.nano;
                const appPlatforms = platformsMap[app.id] || [];
                const legacyPlatforms = Array.isArray(app.platforms) ? app.platforms : [];

                return (
                  <div key={app.id} className="p-3 sm:p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm">{app.full_name}</span>
                          <Badge variant={statusConfig.variant} className="text-[10px] gap-1">{statusConfig.icon}{statusConfig.label}</Badge>
                          <Badge className={`text-[10px] border ${tierColor}`} variant="outline">{app.tier.toUpperCase()}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-xs text-muted-foreground">
                          <span>{app.paypal_email}</span>
                          {(app as any).contact_phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{(app as any).contact_phone}</span>}
                          {app.referral_code && <span className="font-mono">{app.referral_code}</span>}
                          <span>{app.total_followers.toLocaleString()} followers</span>
                          <span>{format(new Date(app.created_at), 'MMM dd, yyyy')}</span>
                          {app.invite_code_used && <span className="font-mono text-amber-600">Inv: {app.invite_code_used}</span>}
                        </div>
                        {(appPlatforms.length > 0 ? appPlatforms : legacyPlatforms).length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {(appPlatforms.length > 0 ? appPlatforms : legacyPlatforms).slice(0, 4).map((p: any, i: number) => (
                              <Badge key={i} variant="outline" className={`text-[9px] ${p.verified_by_admin ? 'border-emerald-500/50 text-emerald-600' : ''}`}>
                                {p.verified_by_admin && '✓ '}{p.platform || p.name || p}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1.5 shrink-0 flex-wrap">
                        <Button variant="outline" size="sm" onClick={() => openDetail(app)} className="text-xs"><Eye className="h-3 w-3 mr-1" />View</Button>
                        <Button variant="outline" size="sm" onClick={() => openEdit(app)} className="text-xs"><Edit className="h-3 w-3 mr-1" />Edit</Button>
                        {app.status === 'pending_verification' && (
                          <>
                            <Button size="sm" onClick={() => updateStatus(app.id, 'approved')} className="text-xs bg-green-600 hover:bg-green-700" disabled={saving}><CheckCircle className="h-3 w-3 mr-1" />Approve</Button>
                            <Button variant="destructive" size="sm" onClick={() => updateStatus(app.id, 'rejected')} className="text-xs" disabled={saving}><XCircle className="h-3 w-3 mr-1" />Reject</Button>
                          </>
                        )}
                        {app.status === 'approved' && (
                          <Button variant="destructive" size="sm" onClick={() => updateStatus(app.id, 'suspended')} className="text-xs" disabled={saving}><Ban className="h-3 w-3 mr-1" />Suspend</Button>
                        )}
                        {app.status === 'suspended' && (
                          <Button size="sm" onClick={() => updateStatus(app.id, 'approved')} className="text-xs bg-green-600 hover:bg-green-700" disabled={saving}><CheckCircle className="h-3 w-3 mr-1" />Reactivate</Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => setAppToDelete({ id: app.id, name: app.full_name })} className="text-xs text-destructive hover:text-destructive" disabled={saving}><Trash2 className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Crown className="h-5 w-5" /> Ambassador Details</DialogTitle>
          </DialogHeader>
          {selectedApp && (() => {
            const appPlats = platformsMap[selectedApp.id] || [];
            const legacyPlats = Array.isArray(selectedApp.platforms) ? selectedApp.platforms : [];
            const displayPlats = appPlats.length > 0 ? appPlats : null;

            return (
              <div className="space-y-3 text-sm">
                <div className="grid grid-cols-2 gap-3">
                  <div><p className="text-muted-foreground text-xs">Name</p><p className="font-medium">{selectedApp.full_name}</p></div>
                  <div><p className="text-muted-foreground text-xs">Status</p><Badge variant={STATUS_CONFIG[selectedApp.status]?.variant || 'secondary'}>{STATUS_CONFIG[selectedApp.status]?.label || selectedApp.status}</Badge></div>
                  <div><p className="text-muted-foreground text-xs">Tier</p><Badge className={`border ${TIER_COLORS[selectedApp.tier]}`} variant="outline">{selectedApp.tier.toUpperCase()}</Badge></div>
                  <div><p className="text-muted-foreground text-xs">Followers</p><p className="font-medium">{selectedApp.total_followers.toLocaleString()}</p></div>
                  <div><p className="text-muted-foreground text-xs">PayPal</p><p className="font-medium truncate">{selectedApp.paypal_email}</p></div>
                  <div><p className="text-muted-foreground text-xs">Referral Code</p><p className="font-mono font-medium">{selectedApp.referral_code || '— (generated on approval)'}</p></div>
                  <div><p className="text-muted-foreground text-xs">ID / Passport</p><p className="font-medium">{selectedApp.id_number}</p></div>
                  <div><p className="text-muted-foreground text-xs flex items-center gap-1"><Phone className="h-3 w-3" /> Contact Phone</p><p className="font-medium">{(selectedApp as any).contact_phone || '—'}</p></div>
                  <div><p className="text-muted-foreground text-xs">Applied On</p><p className="font-medium">{format(new Date(selectedApp.created_at), 'MMM dd, yyyy HH:mm')}</p></div>
                  {selectedApp.approved_at && <div><p className="text-muted-foreground text-xs">Approved On</p><p className="font-medium">{format(new Date(selectedApp.approved_at), 'MMM dd, yyyy HH:mm')}</p></div>}
                  {selectedApp.invite_code_used && <div><p className="text-muted-foreground text-xs">Invite Code Used</p><p className="font-mono font-medium text-amber-600">{selectedApp.invite_code_used}</p></div>}
                  {selectedApp.terms_version && <div><p className="text-muted-foreground text-xs">Terms Version</p><p className="font-medium">{selectedApp.terms_version}</p></div>}
                  {(selectedApp as any).applied_referral_code && <div><p className="text-muted-foreground text-xs">Applied Promo Code</p><p className="font-mono font-medium text-amber-600">{(selectedApp as any).applied_referral_code}</p></div>}
                </div>

                {/* ID Document Viewer */}
                {(selectedApp as any).id_document_url && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/20 border-b flex items-center gap-2">
                      <FileImage className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                      <h4 className="text-sm font-semibold">ID Document</h4>
                    </div>
                    <div className="p-3">
                      <IDDocumentViewer documentPath={(selectedApp as any).id_document_url} />
                    </div>
                  </div>
                )}

                {(displayPlats || legacyPlats.length > 0) && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="p-3 bg-amber-50 dark:bg-amber-950/20 border-b flex items-center gap-2">
                      <ShieldCheck className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                      <h4 className="text-sm font-semibold">Platform Verification</h4>
                      {selectedApp.status === 'pending_verification' && (
                        <Badge variant="outline" className="ml-auto text-[9px] border-yellow-500/30 text-yellow-600 dark:text-yellow-400">
                          <AlertCircle className="h-3 w-3 mr-1" /> Verification Required
                        </Badge>
                      )}
                    </div>
                    <div className="divide-y">
                      {displayPlats ? displayPlats.map(p => (
                        <div key={p.id} className="p-3 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-semibold">{p.platform}</span>
                              {p.is_primary && <Badge variant="outline" className="text-[9px]">Primary</Badge>}
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1.5">
                                <Users className="h-3 w-3 text-muted-foreground" />
                                <span className="text-sm font-medium">{p.followers_count.toLocaleString()}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  checked={p.verified_by_admin}
                                  onCheckedChange={() => togglePlatformVerification(p.id, p.verified_by_admin)}
                                  className="data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500"
                                />
                                <span className={`text-[10px] font-medium ${p.verified_by_admin ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                                  {p.verified_by_admin ? 'Verified' : 'Unverified'}
                                </span>
                              </div>
                            </div>
                          </div>
                          {p.profile_url && (() => {
                            const normalizedUrl = p.profile_url.match(/^https?:\/\//) ? p.profile_url : `https://${p.profile_url}`;
                            return (
                            <div className="flex items-center gap-2">
                              <Link className="h-3 w-3 text-muted-foreground shrink-0" />
                              <a href={normalizedUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline truncate flex-1">{p.profile_url}</a>
                              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => window.open(normalizedUrl, '_blank')}>
                                <ExternalLink className="h-3 w-3 mr-1" /> Open
                              </Button>
                            </div>);
                          })()}
                        </div>
                      )) : legacyPlats.map((p: any, i: number) => (
                        <div key={i} className="p-3 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-semibold">{typeof p === 'object' ? p.name || p.platform : p}</span>
                            <div className="flex items-center gap-1.5">
                              <Users className="h-3 w-3 text-muted-foreground" />
                              <span className="text-sm font-medium">{typeof p === 'object' ? Number(p.followers || 0).toLocaleString() : '0'}</span>
                            </div>
                          </div>
                          {typeof p === 'object' && p.url && (() => {
                            const normalizedLegacyUrl = p.url.match(/^https?:\/\//) ? p.url : `https://${p.url}`;
                            return (
                            <div className="flex items-center gap-2">
                              <Link className="h-3 w-3 text-muted-foreground shrink-0" />
                              <a href={normalizedLegacyUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline truncate flex-1">{p.url}</a>
                              <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px]" onClick={() => window.open(normalizedLegacyUrl, '_blank')}>
                                <ExternalLink className="h-3 w-3 mr-1" /> Open
                              </Button>
                            </div>);
                          })()}
                        </div>
                      ))}
                    </div>
                    <div className="p-3 bg-muted/30 border-t flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">Declared Total</span>
                      <span className="text-sm font-bold">{selectedApp.total_followers.toLocaleString()} followers</span>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 pt-2 flex-wrap">
                  {selectedApp.status === 'pending_verification' && (
                    <>
                      <Button onClick={() => updateStatus(selectedApp.id, 'approved')} className="flex-1 bg-green-600 hover:bg-green-700" disabled={saving}><CheckCircle className="h-4 w-4 mr-1" />Approve</Button>
                      <Button variant="destructive" onClick={() => updateStatus(selectedApp.id, 'rejected')} className="flex-1" disabled={saving}><XCircle className="h-4 w-4 mr-1" />Reject</Button>
                    </>
                  )}
                  {selectedApp.status === 'approved' && (
                    <Button variant="destructive" onClick={() => updateStatus(selectedApp.id, 'suspended')} className="flex-1" disabled={saving}><Ban className="h-4 w-4 mr-1" />Suspend</Button>
                  )}
                  {selectedApp.status === 'suspended' && (
                    <Button onClick={() => updateStatus(selectedApp.id, 'approved')} className="flex-1 bg-green-600 hover:bg-green-700" disabled={saving}><CheckCircle className="h-4 w-4 mr-1" />Reactivate</Button>
                  )}
                   <Button variant="ghost" onClick={() => setAppToDelete({ id: selectedApp.id, name: selectedApp.full_name })} className="text-destructive hover:text-destructive hover:bg-destructive/10" disabled={saving}>
                     <Trash2 className="h-4 w-4 mr-1" /> Delete
                   </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Edit className="h-5 w-5" /> Edit Ambassador</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium mb-1">{selectedApp.full_name}</p>
                <p className="text-xs text-muted-foreground">{selectedApp.paypal_email}</p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Tier</label>
                <Select value={editData.tier} onValueChange={(v) => setEditData(p => ({ ...p, tier: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nano">Nano (1K-9.9K)</SelectItem>
                    <SelectItem value="micro">Micro (10K-49.9K)</SelectItem>
                    <SelectItem value="macro">Macro (50K-499.9K)</SelectItem>
                    <SelectItem value="mega">Mega (500K+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select value={editData.status} onValueChange={(v) => setEditData(p => ({ ...p, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending_verification">Pending</SelectItem>
                    <SelectItem value="approved">Approved</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
                <Button onClick={saveEdit} disabled={saving}>{saving ? t('common.processing') : t('common.save')}</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!codeToDelete} onOpenChange={(open) => !open && setCodeToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Invite Code
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete code <strong>"{codeToDelete?.code}"</strong>?
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (codeToDelete) {
                  deleteInviteCode(codeToDelete.id);
                  setCodeToDelete(null);
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Ambassador Application AlertDialog */}
      <AlertDialog open={!!appToDelete} onOpenChange={(open) => !open && setAppToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              {t('adminPanel.ambassadors.deleteTitle', 'Delete Application')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('adminPanel.ambassadors.confirmDelete', `Are you sure you want to permanently delete the application from "${appToDelete?.name}"?`).replace('{{name}}', appToDelete?.name || '')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeleteApplication}
            >
              {t('common.delete', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminAmbassadorsPanel;
